import { CartElementType } from '@lemonadefashion/cart-element';
import { ItemPriceType } from '@lemonadefashion/item';
import { OrderPriceType } from '@lemonadefashion/order';
import { canonicalURL } from '../utils/canonical_url';

const ADD_TO_FAVORITES_MULTIPLIER = 0.005;
const ADD_TO_CART_MULTIPLIER = 0.02;
const CHECKOUT_MULTIPLIER = 0.1;
const VIEW_CONTENT_MULTIPLIER = 0.001;

const buildItemDataObject = (item, price) => {
  const result = itemDataObject(item);
  // Not all items have a price available to us.
  // This is true for items which are Price-On-Request.
  if (price) {
    // Subtle detail, price is expected to be of type "currency", which is a string.
    // This is a bit strange, but we'll just do it.
    result.price = price.amount.toFixed(2);
  }
  return result;
};

export const pageRouteObject = (fullRoute) => {
  return {
    page_location: canonicalURL(fullRoute),
    page_path: fullRoute
  };
};

export const itemDataObject = (item) => {
  return {
    id: item.getId(),
    name: item.getNameInDefaultLocale() + ' by ' + item.getBrandName(),
    brand: item.getBrandName(),
    category: item.getCategory(),
    quantity: 1
  };
};

export const addToCartItemDataObject = (item, price) => {
  return {
    value: price ? price.amount * ADD_TO_CART_MULTIPLIER : 0.00,
    currency: price ? price.currency : 'USD',
    items: [buildItemDataObject(item, price)]
  };
};

export const removeFromCartItemDataObject = (item, price) => {
  return {
    value: 0.00,
    currency: price ? price.currency : 'USD',
    items: [buildItemDataObject(item, price)]
  };
};

export const addToFavoriteDataObject = (item, price) => {
  return {
    value: price ? price.amount * ADD_TO_FAVORITES_MULTIPLIER : 0.00,
    currency: price ? price.currency : 'USD',
    items: [buildItemDataObject(item, price)]
  };
};

export const itemConversionDataObject = (price) => {
  return {
    send_to: 'AW-607845441/t6XtCMzO--cCEMH466EC',
    value: price ? price.amount * ADD_TO_CART_MULTIPLIER : 0.00,
    currency: price ? price.currency : 'USD'
  };
};

export const trackPurchaseDataObject = (cart, transactionId, i18nSettings, deliveryAddress) => {
  const pricing = cart.getPricing({ useEphemeral: true });
  const orderPrice = pricing.getPrimaryPrice(OrderPriceType.POST_CREDIT_TOTAL);
  const orderAmount = orderPrice.amount;
  const orderCurrency = orderPrice.currency;
  const shipping = pricing.getPrimaryPrice(OrderPriceType.SHIPPING_FEE).amount;
  return {
    transaction_id: transactionId,
    locale: i18nSettings.getLocale(),
    value: orderAmount,
    currency: orderCurrency,
    tax: '0.00',
    shipping: shipping.toFixed(2),
    items: buildCartItemsDataForTrackingObject(cart),
    coupon: pricing.getAppliedPromoCodeName() || '',
    delivery_address: deliveryAddress
  };
};

export const trackPurchaseConversionDataObject = (cart, transactionId) => {
  const pricing = cart.getPricing({ useEphemeral: true });
  const orderPrice = pricing.getPrimaryPrice(OrderPriceType.POST_CREDIT_TOTAL);
  const orderAmount = orderPrice.amount;
  const orderCurrency = orderPrice.currency;
  return {
    send_to: 'AW-607845441/7gooCPnIhvEBEMH466EC',
    transaction_id: transactionId,
    value: orderAmount,
    currency: orderCurrency
  };
};

export const beginCheckoutDataObject = (cart) => {
  const pricing = cart.getPricing({ useEphemeral: true });
  const orderPrice = pricing.getPrimaryPrice(OrderPriceType.POST_CREDIT_TOTAL);
  const orderAmount = orderPrice.amount;
  const orderCurrency = orderPrice.currency;
  return {
    value: orderAmount * CHECKOUT_MULTIPLIER,
    currency: orderCurrency,
    items: buildCartItemsDataForTrackingObject(cart),
    coupon: pricing.getAppliedPromoCodeName() || ''
  };
};

export const beginCheckoutConversionDataObject = (cart) => {
  const pricing = cart.getPricing({ useEphemeral: true });
  const orderPrice = pricing.getPrimaryPrice(OrderPriceType.POST_CREDIT_TOTAL);
  const orderAmount = orderPrice.amount;
  const orderCurrency = orderPrice.currency;
  return {
    send_to: 'AW-607845441/BBd5CNThpegCEMH466EC',
    value: orderAmount * CHECKOUT_MULTIPLIER,
    currency: orderCurrency
  };
};

export const buildCartItemsDataForTrackingObject = (cart) => {
  const items = [];
  for (let i = 0; i < cart.size({ useEphemeral: true }); ++i) {
    const element = cart.get(i, { useEphemeral: true });
    const elementType = element.getElementType();
    switch (elementType) {
      case CartElementType.DESIGNER_BRAND_ITEM: {
        const item = element.getItem();
        items.push({
          id: item.getId(),
          name: item.getNameInDefaultLocale(),
          brand: item.getBrandName(),
          category: item.getCategory(),
          quantity: element.getQuantity(),
          // Subtle detail, price is expected to be of type "currency", which is a string.
          // This is a bit strange, but we'll just do it.
          price: element.getUnitPrimaryPrice(ItemPriceType.SHIPPING_COMPENSATED_PROMO_PRICE).amount.toFixed(2)
        });
        break;
      }
      default:
        throw new Error('Unhandled cart element type: ' + elementType);
    }
  }
  return items;
};

export const checkoutProgressDataObject = (cart, stepNumber, optionName) => {
  const pricing = cart.getPricing({ useEphemeral: true });
  const orderPrice = pricing.getPrimaryPrice(OrderPriceType.POST_CREDIT_TOTAL);
  const orderAmount = orderPrice.amount;
  const orderCurrency = orderPrice.currency;
  return {
    value: orderAmount * CHECKOUT_MULTIPLIER,
    currency: orderCurrency,
    items: buildCartItemsDataForTrackingObject(cart),
    coupon: pricing.getAppliedPromoCodeName() || '',
    checkout_step: stepNumber,
    checkout_option: optionName
  };
};

export const trackViewItemDataObject = (item) => {
  const price = item.getDefaultPrimaryPrice(ItemPriceType.SHIPPING_COMPENSATED_PROMO_PRICE);
  return {
    value: price.amount * VIEW_CONTENT_MULTIPLIER,
    currency: price.currency,
    items: [buildItemDataObject(item, price)]
  };
};

export const trackErrorMessage = (errorMessage) => {
  return {
    error: errorMessage
  };
};

export const searchQueryDataObject = (query) => {
  return {
    search_query: query
  };
};

export const liveStreamMixPanelDataObject = (liveStream, user) => {
  return {
    stream_id: liveStream.id,
    stream_publisher_id: liveStream.owned_by,
    stream_publisher_name: liveStream.publishing_ambassador.name,
    user_id: user?.getId(),
    user_name: user?.getFullName()
  };
};
